import * as React from 'react';
import loadable from '@loadable/component';
import { useFeatures } from '@paralleldrive/react-feature-toggles';
import { CruisePageTabs, ICruisePageTabs } from '../../../components/CruisePageTabs/CruisePageTabs';
import ReviewsDesktop from '../../../components/DesktopReviews/DesktopReviews';
import { mapMediaToGallery, getRouteImage } from '../../../components/Gallery/Gallery';
import { CabinIcon2 } from '../../../components/Icons/CabinIcon2';
import { LevelIcon } from '../../../components/Icons/newIcons/LevelIcon';
import { DashboardIcon } from '../../../components/Icons/newIcons/DashboardIcon';
import { RatingsIcon } from '../../../components/Icons/RatingsIcon';
import { RouteIcon } from '../../../components/Icons/newIcons/RouteIcon';
import { ShipIcon } from '../../../components/Icons/newIcons/ShipIcon';
import { StarIcon } from '../../../components/Icons/newIcons/StarIcon';
import ShipsView from '../../../components/ShipsView/ShipsView';
import { eventTracker } from '../../../helpers/EventTracker/EventTracker';
import { __ } from '../../../helpers/TranslationService';
import { useActions } from '../../../hooks/useActions';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import Cabins from '../components/Cabins/Cabins';
import Decks from '../components/Decks/Decks';
import Waypoints from '../components/Waypoints';
import CruiseStageOverview from '../CruiseStage/CruiseStageOverview';
import CruiseStageHeader from './CruiseStageHeader';
import CruiseStageTabHeader from './CruiseStageTabHeader';
import DreamLinesPackageBanner from '../components/DreamLinesPackageBanner';
import { IPackageCruise } from '../../../interfaces';
import { packageCruiseEnabled } from '../../../helpers/packageCruise';
import { SE_TOURS_COMPANY_ID } from '../../../consts/variables';

const RoutesMapList = loadable(() => import('../../../components/RoutesMapList/RoutesMapList'), {
    ssr: false,
});

export interface ICruiseStageProps {
    startHarbour?: string;
    endHarbour?: string;
    isDesktop: boolean;
    packageCruise: IPackageCruise | null;
    currency: string;
    countryCode: string;
}

const trackTabClick = (tabLabel, title) =>
    eventTracker.track({
        category: 'cruisepage-infobox',
        action: tabLabel,
        label: title,
    });

export const CruiseStage = (props: ICruiseStageProps): JSX.Element => {
    const { startHarbour, endHarbour, isDesktop, packageCruise, currency, countryCode } = props;

    const { cruisePage, reviews: reviewsData, pageData } = useTypedSelector((state) => state);
    const { reviews, sortString, isLoading: areReviewsLoading } = reviewsData || {};
    const { appConfig } = pageData;
    const { defaultCurrency: priceCurrency } = appConfig;
    const { loadMoreUGCReviews, setReviewSorting } = useActions();
    const features = useFeatures();
    const cruisePageTabsRef = React.useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = React.useState(undefined);
    React.useEffect(() => {
        if (activeTab !== undefined) {
            window.scrollTo({
                top: cruisePageTabsRef?.current?.offsetTop - 77,
                left: 0,
                behavior: 'smooth',
            });
        }
    }, [activeTab]);

    const {
        nights,
        ship,
        company,
        zone,
        images,
        thumbnails = [],
        videoThumbPaths = [],
        videoUrls = [],
        routeImage,
        operator,
        nid,
        offers,
        ugcLabels,
        breadcrumbs,
        icons,
        dateRange,
        sails,
        isMapImageEnabled,
    } = cruisePage;

    const nightsString = nights > 1 ? __('Nights', 'dreamlines') : __('Night', 'dreamlines');
    const dreamDeal = cruisePage.specials.filter((el) => el.isDreamDeal)[0] || false;
    const isSeTours = company.nid === SE_TOURS_COMPANY_ID;
    const useTitleFromBackend = operator?.isDreamlinesPackage || isSeTours || !zone;
    const hasRouteImage = isMapImageEnabled && Boolean(routeImage);

    const cruisePageTitle = useTitleFromBackend
        ? cruisePage.title
        : `${zone} ${__('from', 'dreamlines')} ${startHarbour}`;

    let tabs: ICruisePageTabs[] = null;

    if (isDesktop) {
        const mediaGallery = mapMediaToGallery(
            images,
            thumbnails,
            isMapImageEnabled === false ? null : getRouteImage(routeImage, countryCode, nid),
            videoUrls,
            videoThumbPaths,
        );

        tabs = [
            {
                icon: () => <DashboardIcon />,
                title: __('Overview', 'dreamlines'),
                content: () => (
                    <CruiseStageOverview
                        isDesktop={isDesktop}
                        gallery={mediaGallery}
                        zone={zone}
                        companyTitle={company?.title}
                        shipTitle={ship?.title}
                        startHarbour={startHarbour}
                        endHarbour={endHarbour}
                        nightsString={nights > 0 ? `${nights} ${nightsString}` : ''}
                        dateRange={dateRange}
                        icons={icons}
                        cruiseId={nid}
                        cruiseTitle={cruisePageTitle}
                        dreamDeal={dreamDeal}
                        sails={sails}
                        packageCruise={packageCruise}
                        currency={currency}
                        countryCode={countryCode}
                        setActiveCruisePageTab={setActiveTab}
                        hasRouteImage={hasRouteImage}
                    />
                ),
                isActive: true,
                trackingFunction: () => trackTabClick('overview', cruisePageTitle),
            },
            {
                icon: () => <RouteIcon />,
                title: __('Itinerary', 'dreamlines'),
                content: () => (
                    <>
                        <CruiseStageTabHeader
                            title={__('route & map', 'dreamlines')}
                            isPriceBtnVisible={true}
                        />
                        {isDesktop ? <RoutesMapList /> : <Waypoints />}
                    </>
                ),
                isActive: true,
                trackingFunction: () => trackTabClick('itinerary', cruisePageTitle),
            },
            {
                icon: () => <ShipIcon viewBox="0 0 50 50" />,
                title: __('Ship', 'dreamlines'),
                content: () => (
                    <>
                        <CruiseStageTabHeader
                            descr={__('Get to know everything about the ship', 'dreamlines')}
                            isPriceBtnVisible={true}
                        />
                        <ShipsView
                            facts={ship?.factsAndFigures?.toString()}
                            shipImage={images.length > 0 ? images[0] : null}
                            description={ship?.description?.toString()}
                        />
                    </>
                ),
                isActive: true,
                trackingFunction: () => trackTabClick('ship', cruisePageTitle),
            },
            {
                icon: () => <LevelIcon />,
                title: __('Decks', 'dreamlines'),
                content: () => {
                    return (
                        <>
                            <CruiseStageTabHeader
                                title={__('DECK PLANS', 'dreamlines')}
                                descr={ship.title}
                                isPriceBtnVisible={true}
                            />
                            <Decks />
                        </>
                    );
                },
                isActive: true,
                trackingFunction: () => trackTabClick('decks', cruisePageTitle),
            },
            {
                icon: () => <CabinIcon2 viewBox="0 0 50 50" />,
                title: __('Cabins', 'dreamlines'),
                content: () => (
                    <>
                        <CruiseStageTabHeader
                            title={__('CABINS', 'dreamlines')}
                            descr={ship.title}
                        />
                        <Cabins />
                    </>
                ),
                isActive: true,
                trackingFunction: () => trackTabClick('cabins', cruisePageTitle),
            },
            {
                icon: () => <StarIcon />,
                title: __('Reviews', 'dreamlines'),
                content: () => (
                    <>
                        <CruiseStageTabHeader isPriceBtnVisible={true} />
                        <ReviewsDesktop
                            reviews={reviews}
                            offers={offers}
                            priceCurrency={priceCurrency}
                            loadMoreReviews={loadMoreUGCReviews}
                            setSortString={setReviewSorting}
                            sortString={sortString}
                            filterParams={`filterParams[shipNid]=${ship.nid}`}
                            reviewsType={'shipReviews'}
                            shipTitle={ship.title}
                            googleScriptName={ship.title}
                            ugcLabels={ugcLabels}
                            isLoading={areReviewsLoading}
                            collapseComments={true}
                            disableGoogleScript
                        />
                    </>
                ),
                isActive:
                    features.indexOf('showReviews') >= 0 &&
                    reviews?.aggregation !== null &&
                    reviews?.rating !== null &&
                    reviews.rating.data.length > 0,
                trackingFunction: () => trackTabClick('ship-rating', cruisePageTitle),
            },
        ];
    }

    return (
        <div className="cruise-stage">
            <CruiseStageHeader
                nid={nid}
                title={cruisePageTitle}
                stars={ship.category}
                companyLogo={company?.logo}
                companyTitle={company?.title}
                packageOperatorLogo={operator?.logo}
                packageOperatorTitle={operator?.title}
                breadcrumbs={breadcrumbs}
                icons={icons}
                isDreamlinesPackage={operator?.isDreamlinesPackage}
                nights={nights}
                waypoints={cruisePage.waypoints}
            />
            {packageCruise && packageCruiseEnabled(pageData.appConfig.countryCode) && (
                <DreamLinesPackageBanner
                    withMessage={false}
                    currency={currency}
                    packageCruise={packageCruise}
                />
            )}
            {isDesktop && (
                <CruisePageTabs
                    ref={cruisePageTabsRef}
                    cruiseNid={cruisePage.nid}
                    tabs={tabs}
                    activeTabIndex={activeTab}
                />
            )}
        </div>
    );
};
